import { createContext, useContext, ReactNode } from 'react';
import { ACHFormValue, defaultFormValue } from 'dibs-ach-form/exports/ACHForm';
import { ACHFormType } from 'dibs-ach-form/exports/ACHValidate';
import type { PaymentMethod, PaymentMethodOption, Address, ChargeCurrency } from '../types';

export type PaymentMethodConfigOptions = {
    WIRE_TRANSFER?: {
        selectedContent?: () => ReactNode;
    };
    ON_PLATFORM_WIRE_TRANSFER?: {
        selectedContent?: () => ReactNode;
    };
    APPLE_PAY?: {
        selectedContent?: () => ReactNode;
    };
    PAYPAL_ACCOUNT?: {
        verifyPaymentMethod?: boolean;
        onVault?: (paymentMethod?: PaymentMethod) => void;
        onSetupError?: (error: unknown) => void;
        onVaultError?: (error: unknown) => void;
    };
    KLARNA?: {
        selectedContent?: () => ReactNode;
    };
    CREDIT_CARD?: {
        verifyPaymentMethod?: boolean;
        cardHolderName?: string;
        additionalFormContent?: () => ReactNode;
    };
    ACH?: {
        verifyPaymentMethod?: boolean;
    };
};

export type CreditCardForm = {
    isVerifying?: boolean;
    cardHolderName?: string;
    creditCardNumber?: string;
    cardExpiry?: string;
    securityCode?: string;
};

export type AchForm = ACHFormValue & { isVerifying?: boolean };
export type AchFormErrors = ACHFormType;

type PaymentMethodSelectContextType = {
    paymentMethods: PaymentMethodOption[];
    stripePaymentMethods: string[];
    chargeCurrency: ChargeCurrency;
    selectedPaymentMethod: PaymentMethod | null;
    userId?: string | null;
    shippingAddressId?: string;
    billingAddress?: Address;
    updateSelectedPaymentMethod: (paymentMethod: PaymentMethod) => void;
    isBusy: boolean;
    setIsBusy: (isBusy: boolean) => void;
    mockWallet: boolean;
    hideWallet: boolean;

    braintreeClientToken?: string;
    viewerId?: string;

    creditCardForm: CreditCardForm;
    creditCardFormErrors: CreditCardForm;
    updateCreditCardForm: (form: Partial<CreditCardForm>) => void;
    updateCreditCardFormErrors: (errors: Partial<CreditCardForm>) => void;

    achForm: AchForm;
    achFormErrors: AchFormErrors;
    updateAchForm: (form: Partial<AchForm>) => void;
    updateAchFormErrors: (errors: Partial<AchFormErrors>) => void;

    paymentMethodOptions?: PaymentMethodConfigOptions;
};

export const PaymentMethodSelectContext = createContext<PaymentMethodSelectContextType>({
    paymentMethods: [],
    stripePaymentMethods: [],
    chargeCurrency: 'USD',
    selectedPaymentMethod: null,
    updateSelectedPaymentMethod: () => {},
    isBusy: false,
    setIsBusy: () => {},
    mockWallet: false,
    hideWallet: false,
    creditCardForm: {},
    creditCardFormErrors: {},
    updateCreditCardForm: () => {},
    updateCreditCardFormErrors: () => {},
    achForm: defaultFormValue,
    achFormErrors: {},
    updateAchForm: () => {},
    updateAchFormErrors: () => {},
});

export function usePaymentMethodSelectContext(): PaymentMethodSelectContextType {
    return useContext(PaymentMethodSelectContext);
}
